<template>
    <div class="bulk-transfer-out-component">
        <div class="filters-grid">
            <div class="filter-field">
                <b-form-group>
                    <b-badge v-if="!selectedStore?.id" variant="warning" class="pull-right throb-badge"
                        style="margin-top:-2px;">
                        <i class="fas fa-exclamation-triangle" />
                        Please Select a Store
                    </b-badge>
                    <SearchComponent v-model="selectedStore" search-u-r-l="/api/Store" label="Store"
                        placeholder="Select Store..." :id="storeId" prepopulate />
                </b-form-group>
            </div>
            <div class="filter-field" v-if="showFilters || patient">
                <b-form-group>
                    <SearchComponent v-model="filterPatient" search-u-r-l="/Patient" label="Patient"
                        :clearable="!patient" :disabled="!!patient" class="pt-0" />
                </b-form-group>
            </div>
            <div class="filter-field">
                <b-form-group label="Drug Name:">
                    <b-form-input type="search" v-model="filterDrugName" @keyup.enter="onManualFilterChange" @input="onSearchFieldInput" />
                </b-form-group>
            </div>
            <div class="filter-field">
                <b-form-group label="Rx Number:">
                    <b-form-input type="search" v-model="filterRxNumber" @keyup.enter="onManualFilterChange" @input="onSearchFieldInput" />
                </b-form-group>
            </div>
            <div class="filter-field">
                <b-form-group label="Status:">
                    <b-form-select v-model="filterStatus" @change="onStatusFilterChange">
                        <b-form-select-option :value="null">All Statuses</b-form-select-option>
                        <b-form-select-option :value="0">Refillable</b-form-select-option>
                        <b-form-select-option :value="1">Auto Refillable</b-form-select-option>
                        <b-form-select-option :value="2">Non Refillable</b-form-select-option>
                        <b-form-select-option :value="3">Suspended</b-form-select-option>
                        <b-form-select-option :value="5">Transfer In</b-form-select-option>
                        <b-form-select-option :value="6">On Hold</b-form-select-option>
                        <b-form-select-option :value="7">Canceled</b-form-select-option>
                        <b-form-select-option :value="10">Awaiting Insurance</b-form-select-option>
                        <b-form-select-option :value="11">Discontinued</b-form-select-option>
                    </b-form-select>
                </b-form-group>
            </div>
            <div class="filter-field" v-if="showSubStatusFilter">
                <b-form-group label="Substatus:">
                    <b-form-select v-model="filterSubStatus" @change="onManualFilterChange">
                        <b-form-select-option :value="null">All Substatuses</b-form-select-option>
                        <b-form-select-option v-for="substatus in applicableSubStatuses" :key="substatus.value"
                            :value="substatus.value" :disabled="!substatus.active">
                            {{ substatus.label }}
                        </b-form-select-option>
                    </b-form-select>
                </b-form-group>
            </div>
            <div class="filter-field">
                <b-form-group>
                    <SearchComponent v-model="filterProgram" search-u-r-l="/api/Program" label="Program"
                        :clearable="true" prepopulate />
                </b-form-group>
            </div>
        </div>

        <b-table :items="prescriptions" :fields="fields" :busy="loading" show-empty :empty-text="emptyText"
            ref="prescriptionTable" @row-clicked="toggleRowSelection" :tbody-tr-class="getRowClass"
            :no-local-sorting="true" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc" @sort-changed="onSortChanged">

            <template #table-busy>
                <div class="text-center my-2">
                    <b-spinner class="align-middle mr-2"></b-spinner>
                    <strong>Loading prescriptions...</strong>
                </div>
            </template>

            <template #head(select)>
                <b-checkbox :checked="isAllSelected" :indeterminate="isSomeSelected" @change="toggleSelectAll"
                    :disabled="!eligiblePrescriptions.length" />
            </template>

            <template #cell(select)="data">
                <b-checkbox :value="data.item" v-model="selectedPrescriptions"
                    :disabled="!canTransferPrescription(data.item)"
                    @click.native.stop="handleCheckboxClick($event, data.item, data.index)" />
            </template>

            <template #cell(rxNumber)="data">
                <router-link :to="{ name: 'PrescriptionDetailsId', params: { rxId: data.item.rxID } }" target="_blank">
                    <strong v-if="data.item.storeID && data.item.rxNumber != null && data.item.rfNumber != null">
                        {{ data.item.storeID }}-{{ data.item.rxNumber }}-{{ data.item.rfNumber }}
                    </strong>
                    <span v-else class="text-muted">N/A</span>
                </router-link>
            </template>

            <template #cell(patientName)="data">
                <router-link :to="{ name: 'PatientDashboard', params: { id: data.item.patient?.id } }" target="_blank">
                    <strong>
                        {{ data.item.patient?.firstName }}
                        {{ data.item.patient?.lastName }}
                    </strong>
                </router-link>
                <div v-if="data.item.patient?.addressCity && data.item.patient?.addressState">
                    <small>
                        {{ data.item.patient.addressCity }}, {{ data.item.patient.addressState }}
                    </small>
                </div>
            </template>

            <template #cell(status)="data">
                {{ data.item.statusName }}
                <div v-if="data.item.subStatusName">
                    <small class="text-muted">{{ data.item.subStatusName }}</small>
                </div>
            </template>

            <template #cell(programName)="data">
                {{ data.item.programName }}
            </template>

            <template #cell(prescriberName)="data">
                {{ data.item.prescriber?.firstName }} {{ data.item.prescriber?.lastName }}
                <div v-if="data.item.prescriber?.addressCity && data.item.prescriber?.addressState">
                    <small>
                        {{ data.item.prescriber.addressCity }}, {{ data.item.prescriber.addressState }}
                    </small>
                </div>
            </template>

            <template #cell(actions)="data">
                <b-button v-if="!canTransferPrescription(data.item)" size="sm" variant="outline-danger" disabled
                    v-b-tooltip.hover :title="getIneligibilityReason(data.item)">
                    <i class="fa fa-ban"></i>
                    Cannot Transfer
                </b-button>
            </template>
        </b-table>

        <BottomPagerBar v-model="currentPage" :total-rows="totalRows" :per-page="perPage"
            :selected-rows="selectedPrescriptions.length" :loading="loading" item-name="prescription" show-total
            show-page-size-selector @change-page-size="onPageSizeChange" @clear-selection="selectedPrescriptions = []">

            <template #fab>
                <b-button v-if="selectedPrescriptions.length" variant="success" :disabled="isTransferring"
                    @click="startTransfer">
                    <b-spinner v-if="isTransferring" small class="mr-1" />
                    <i v-else class="fa fa-exchange-alt mr-1"></i>
                    Transfer {{ selectedPrescriptions.length }} Prescription{{ selectedPrescriptions.length !== 1 ? 's'
                        : '' }}
                </b-button>
            </template>
        </BottomPagerBar>

        <!-- External Transfer Modal -->
        <ExternalTransferModal ref="transferModal" :prescriptions="selectedPrescriptions" :store="selectedStore"
            @transferred="onTransferCompleted" @closed="onTransferClosed" />
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import Axios from 'axios';
import SearchComponent from '@/components/SearchComponent.vue';
import BottomPagerBar from '@/components/BottomPagerBar.vue';
import ExternalTransferModal from '@/components/Transfer/ExternalTransferModal.vue';
import { Prescription, RefillStatusCode } from '@/models/Prescription';
import { Store } from '@/models/Store';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { Patient } from '@/models/Patient';
import { Program } from '@/models/Program';

@Component({
    name: 'BulkTransferOutComponent',
    components: {
        SearchComponent,
        BottomPagerBar,
        ExternalTransferModal
    }
})
export default class BulkTransferOutComponent extends Vue {
    @Prop({ default: true }) showFilters!: boolean;
    @Prop() patient?: Patient;
    @Prop() storeId?: number;

    private filterPatient: Patient = new Patient();
    private filterDrugName: string = '';
    private filterRxNumber: string = '';
    private filterStatus: number | null = null;
    private filterSubStatus: number | null = null;
    private filterProgram: Program = new Program();
    private prescriptions: Prescription[] = [];
    private selectedPrescriptions: Prescription[] = [];
    private selectedStore: Store = new Store();
    private currentPage: number = 1;
    private perPage: number = 25;
    private totalRows: number = 0;
    private loading: boolean = false;
    private isTransferring: boolean = false;
    private sortBy: string = '';
    private sortDesc: boolean = true;
    private onHoldSubStatuses: Array<{ value: number, label: string, active: boolean }> = [];
    private discontinuedSubStatuses: Array<{ value: number, label: string, active: boolean }> = [];
    private lastSelectedIndex: number = -1;
    private isInitializing: boolean = true;
    private lastFetchParams: string = '';
    private cancelTokenSource: any = null;

    get fields() {
        const allFields = [
            { key: 'select', label: '', sortable: false, thStyle: { width: '60px' } },
            { key: 'rxNumber', label: 'Rx #', sortable: true },
            { key: 'patientName', label: 'Patient', sortable: true },
            { key: 'prescriberName', label: 'Prescriber', sortable: true },
            { key: 'drugName', label: 'Drug', sortable: true },
            { key: 'programName', label: 'Program', sortable: false },
            { key: 'quantity', label: 'Qty', sortable: false },
            { key: 'refills', label: 'Refills', sortable: false },
            { key: 'remainingQuantity', label: 'Qty Remaining', sortable: false },
            { key: 'status', label: 'Status', sortable: false },
            { key: 'actions', label: '', sortable: false, thStyle: { width: '150px' } }
        ];

        // Filter out columns based on conditions
        return allFields.filter(field => {
            // Hide patient column if patient is passed as prop
            if (field.key === 'patientName' && this.patient) return false;
            return true;
        });
    }

    get emptyText() {
        if (!this.selectedStore?.id) {
            return "Please select a store to view available prescriptions for transfer";
        }
        return "No prescriptions available for transfer";
    }

    get showSubStatusFilter(): boolean {
        return this.filterStatus === RefillStatusCode.OnHold || this.filterStatus === RefillStatusCode.Discontinued;
    }

    get applicableSubStatuses() {
        return this.filterStatus === RefillStatusCode.OnHold ? this.onHoldSubStatuses : this.discontinuedSubStatuses;
    }

    get eligiblePrescriptions(): Prescription[] {
        return this.prescriptions.filter(p => this.canTransferPrescription(p));
    }

    get isAllSelected(): boolean {
        if (!this.eligiblePrescriptions.length) return false;
        return this.eligiblePrescriptions.every(p => this.selectedPrescriptions.some(sp => sp.rxID === p.rxID));
    }

    get isSomeSelected(): boolean {
        if (!this.eligiblePrescriptions.length) return false;
        const selectedCount = this.eligiblePrescriptions.filter(p =>
            this.selectedPrescriptions.some(sp => sp.rxID === p.rxID)
        ).length;
        return selectedCount > 0 && selectedCount < this.eligiblePrescriptions.length;
    }

    get lastStore(): any {
        const objStr = localStorage.getItem('bulkTransfer_lastStore');
        if (objStr)
            return JSON.parse(objStr);
        else return null;
    }

    @Watch('selectedStore')
    lastStoreChanged(val1: any, val2: any) {
        if (val1 == new Store()) return;
        localStorage.setItem('bulkTransfer_lastStore', JSON.stringify(val1));

        // If we're initializing with a storeId prop and the store just got loaded, trigger the fetch
        if (this.isInitializing && this.storeId && val1?.id) {
            this.isInitializing = false;
            this.currentPage = 1;
            this.selectedPrescriptions = [];
            this.lastFetchParams = '';
            this.fetchPrescriptions();
        }
    }

    async mounted() {
        if (!this.storeId && this.lastStore) {
            this.selectedStore = new Store(this.lastStore);
        }

        await this.getSubStatusConfiguration();

        if (this.patient) {
            this.filterPatient = this.patient;
        }

        if (!this.storeId) {
            await this.$nextTick();
            this.isInitializing = false;
            if (this.selectedStore?.id) {
                this.currentPage = 1;
                this.selectedPrescriptions = [];
                this.lastFetchParams = '';
                this.fetchPrescriptions();
            }
        }
    }

    async getSubStatusConfiguration() {
        try {
            const resp = await Axios.get("/api/Configuration");
            this.onHoldSubStatuses = resp.data.onHoldSubStatuses.map((ss: any) => ({
                value: ss.id,
                label: ss.name,
                active: ss.active,
            }));
            this.discontinuedSubStatuses = resp.data.discontinuedSubStatuses.map((ss: any) => ({
                value: ss.id,
                label: ss.name,
                active: ss.active,
            }));
        } catch (error) {
            console.error("Failed to load substatus configuration", error);
        }
    }

    @Watch('filterPatient')
    @Watch('filterProgram')
    @Watch('selectedStore')
    onFiltersChanged() {
        if (this.isInitializing) return;

        this.currentPage = 1;
        this.selectedPrescriptions = [];
        this.fetchPrescriptions();
    }

    @Watch('currentPage')
    onPageChanged() {
        this.fetchPrescriptions();
    }

    async fetchPrescriptions() {
        try {
            if (this.cancelTokenSource) {
                this.cancelTokenSource.cancel('New request initiated');
            }

            this.cancelTokenSource = Axios.CancelToken.source();
            this.loading = true;

            if (!this.selectedStore?.id) {
                this.prescriptions = [];
                this.totalRows = 0;
                this.lastFetchParams = '';
                return;
            }

            const params = {
                storeId: this.selectedStore.id,
                page: this.currentPage,
                pageSize: this.perPage,
                patientId: this.filterPatient?.id,
                drugName: this.filterDrugName,
                rxNumber: this.filterRxNumber,
                status: this.filterStatus,
                subStatus: this.filterSubStatus,
                programId: this.filterProgram?.id || undefined,
                sortBy: this.sortBy,
                sortDesc: this.sortDesc
            };

            // Check if params have changed to avoid duplicate fetches
            const paramsKey = JSON.stringify(params);
            if (paramsKey === this.lastFetchParams && this.lastFetchParams !== '') {
                this.loading = false;
                return;
            }
            this.lastFetchParams = paramsKey;

            const response = await Axios.get('/api/Prescription/bulk-transfer-eligible', {
                params,
                cancelToken: this.cancelTokenSource.token
            });

            this.prescriptions = response.data.prescriptions.map((p: any) => new Prescription({ ...p, prescriberName: p.prescriber?.lastName }));
            this.totalRows = response.data.totalCount;
        } catch (error) {
            if (!Axios.isCancel(error)) {
                this.$notification(NotificationOptions.error(error));
            }
            this.prescriptions = [];
            this.totalRows = 0;
        } finally {
            this.loading = false;
        }
    }

    canTransferPrescription(prescription: Prescription): boolean {
        if (prescription.status === 4) return false;
        if (prescription.claims?.some(claim => claim.transactionStatus === "Paid")) return false;
        return true;
    }

    getIneligibilityReason(prescription: Prescription): string {
        if (prescription.status === 4) {
            return "Prescription has been voided and cannot be transferred";
        }
        if (prescription.claims?.some(claim => claim.transactionStatus === "Paid")) {
            return "Prescription has been billed and cannot be transferred";
        }
        return "Prescription is not eligible for transfer";
    }

    getStatusVariant(status: number): string {
        switch (status) {
            case 0: return 'secondary';
            case 1: return 'primary';
            case 2: return 'success';
            case 3: return 'warning';
            case 4: return 'danger';
            default: return 'light';
        }
    }

    onPageSizeChange(newSize: number) {
        this.perPage = newSize;
        this.currentPage = 1;
        this.fetchPrescriptions();
    }

    startTransfer() {
        if (!this.selectedPrescriptions.length || !this.selectedStore?.id) return;
        this.isTransferring = true;
        this.$refs.transferModal?.$bvModal.show('external-transfer-modal');
    }

    onTransferCompleted(result: any) {
        this.selectedPrescriptions = [];
        this.fetchPrescriptions();
        this.$emit('transfer-completed', result);
    }

    onTransferClosed() {
        this.isTransferring = false;
    }

    onManualFilterChange() {
        this.currentPage = 1;
        this.selectedPrescriptions = [];
        this.fetchPrescriptions();
    }

    private searchDebounce: number | null = null;
    onSearchFieldInput() {
        if (this.searchDebounce) {
            clearTimeout(this.searchDebounce);
        }

        this.searchDebounce = setTimeout(() => {
            this.onManualFilterChange();
        }, 500);
    }

    toggleRowSelection(item: Prescription, index?: number) {
        if (!this.canTransferPrescription(item)) return;

        const selectedIndex = this.selectedPrescriptions.findIndex(p => p.rxID === item.rxID);
        if (selectedIndex > -1) {
            this.selectedPrescriptions.splice(selectedIndex, 1);
        } else {
            this.selectedPrescriptions.push(item);
        }

        if (index !== undefined) {
            this.lastSelectedIndex = index;
        }
    }

    handleCheckboxClick(event: MouseEvent, item: Prescription, index: number) {
        if (!this.canTransferPrescription(item)) return;

        if (event.shiftKey && this.lastSelectedIndex !== -1 && this.lastSelectedIndex !== index) {
            event.preventDefault();

            if (window.getSelection) {
                window.getSelection()?.removeAllRanges();
            }

            const start = Math.min(this.lastSelectedIndex, index);
            const end = Math.max(this.lastSelectedIndex, index);

            for (let i = start; i <= end; i++) {
                const prescription = this.prescriptions[i];
                if (prescription && this.canTransferPrescription(prescription)) {
                    const isSelected = this.selectedPrescriptions.some(p => p.rxID === prescription.rxID);
                    if (!isSelected) {
                        this.selectedPrescriptions.push(prescription);
                    }
                }
            }
        } else {
            this.lastSelectedIndex = index;
        }
    }

    toggleSelectAll() {
        if (this.isAllSelected) {
            this.eligiblePrescriptions.forEach(p => {
                const index = this.selectedPrescriptions.findIndex(sp => sp.rxID === p.rxID);
                if (index > -1) {
                    this.selectedPrescriptions.splice(index, 1);
                }
            });
        } else {
            this.eligiblePrescriptions.forEach(p => {
                const isSelected = this.selectedPrescriptions.some(sp => sp.rxID === p.rxID);
                if (!isSelected) {
                    this.selectedPrescriptions.push(p);
                }
            });
        }
    }

    onStatusFilterChange() {
        this.filterSubStatus = null;
        this.onManualFilterChange();
    }

    getRowClass(item: Prescription) {
        const isSelected = this.selectedPrescriptions.some(p => p.rxID === item.rxID);
        return isSelected ? 'table-primary' : '';
    }

    onSortChanged(ctx: any) {
        this.sortBy = ctx.sortBy;
        this.sortDesc = ctx.sortDesc;
        this.currentPage = 1;
        this.selectedPrescriptions = [];
        this.fetchPrescriptions();
    }
}
</script>

<style scoped>
.filters-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
}

.filter-field {
    min-width: 250px;
}

.filters-grid::v-deep .form-group label {
    margin-bottom: calc(0.375rem + 1px);
    line-height: 1.5;
}

.bulk-transfer-out-component>>>tbody tr {
    cursor: pointer;
}

@keyframes throb {

    0%,
    100% {
        transform: scale(1);
        opacity: 0.9;
    }

    50% {
        transform: scale(1.03);
        opacity: 1;
    }
}

.throb-badge {
    animation: throb 1.5s ease-in-out infinite;
}
</style>